import {
  along,
  featureCollection,
  featureEach,
  getType,
  length,
} from '@turf/turf';
import { Feature, FeatureCollection, LineString, Point } from 'geojson';
import { SHARED_GENERATE_INPUTS } from './reversePointsAndRows';
import { geometryTypes, NONE } from '../../constants/fields';
import {
  cropNameForRow,
  getCropAndSpacingAtIdx,
  patternNameForRow,
} from './rows';
import { feetPerMile } from '../../constants/units';
import {
  DataMaps,
  PointsAndRows,
  PointsAndRowsProps,
} from '../../types/pointsAndRows';
import { unitsMiles } from '../units';

interface GenerateRowPointsProps extends PointsAndRowsProps {
  rows: FeatureCollection<LineString>;
}

export const GENERATE_ROWS_INPUTS = [
  'rows',
  'inRowSpacing',
  'rowOverrides',
  'crop',
  'primaryInRowPattern',
  ...SHARED_GENERATE_INPUTS,
];

/**
 * Generates points within rows at a specified inRowSpacing (in feet).
 */
const generateRowPoints = (
  // Update GENERATE_ROWS_INPUTS above if these params change
  {
    rows,
    inRowSpacing,
    rowOverrides = [],
    crop,
    primaryInRowPattern,
  }: GenerateRowPointsProps,
  dataMaps: DataMaps,
) => {
  // Input Validation
  if (!rows) return {} as PointsAndRows;
  if (!inRowSpacing && !primaryInRowPattern)
    throw new Error('Requires inRowSpacing or primaryInRowPattern');
  if (getType(rows) !== 'FeatureCollection') {
    throw new Error('Rows must be a FeatureCollection');
  }

  const rowsWithOverrides = <Feature<LineString>[]>[];
  const points = <Feature<Point>[]>[];
  featureEach(rows, (row, i) => {
    if (getType(row) !== geometryTypes.LINE_STRING) {
      throw new Error('Rows must only contain LineStrings');
    }
    const rowLengthMiles = length(row, unitsMiles);
    const override = rowOverrides[i];
    let treeCount = 0;
    let placementMiles = 0;
    let nextPointFits = true;

    // No points are generated for NONE rows
    if (override?.cropName !== NONE) {
      while (nextPointFits) {
        const { spacing: nextSpacingFt, cropName } = getCropAndSpacingAtIdx(
          override,
          { inRowSpacing, primaryInRowPattern, crop },
          treeCount,
          dataMaps,
        );

        const nextPlacementMiles = placementMiles + nextSpacingFt / feetPerMile;
        // Add a small delta (0.000001 miles = 0.06336 inches)
        // to account for precision discrepancies between length and along.
        // This ensures that the last point is included.
        nextPointFits = nextPlacementMiles <= rowLengthMiles + 0.000001;

        points.push({
          ...along(row, placementMiles, unitsMiles),
          properties: {
            row: i,
            cropName,
          },
        });
        treeCount += 1;
        placementMiles = nextPlacementMiles;
      }
    }
    rowsWithOverrides.push({
      ...row,
      properties: {
        ...row.properties,
        row: i,
        treeCount,
        rowLength: rowLengthMiles * feetPerMile,
        cropName: cropNameForRow(override, { crop, primaryInRowPattern }),
        patternName: patternNameForRow(
          override,
          { primaryInRowPattern },
          dataMaps,
        ),
      },
    });
  });

  return {
    points: featureCollection(points),
    rows: featureCollection(rowsWithOverrides),
  };
};

export default generateRowPoints;
